import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Send, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../utils/api';

export default function AdminAddBalance() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data.data || []);
    } catch (error) {
      setErrorMessage(t('Failed to load users'));
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddBalance = async () => {
    if (!selectedUser || !amount) {
      setErrorMessage(t('Please select a user and enter an amount'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/add-balance', {
        user_id: selectedUser.id,
        amount: parseFloat(amount),
        custom_message: message,
      });
      setSuccessMessage(t('Balance added successfully'));
      setSelectedUser(null);
      setAmount('');
      setMessage('');
      setShowConfirm(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || t('Failed to add balance'));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedUser(null);
    setAmount('');
    setMessage('');
    setErrorMessage('');
    setSuccessMessage('');
  };


  return (
    <AdminLayout>
      <div dir={isRTL ? 'rtl' : 'ltr'} className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{t('Add Balance to Users')}</h1>
          <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">{t('Manage user account balances and credits')}</p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg flex items-center gap-3 text-sm sm:text-base">
            <CheckCircle size={18} className="sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
            <p className="text-emerald-300">{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-center gap-3 text-sm sm:text-base">
            <AlertCircle size={18} className="sm:w-5 sm:h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300">{errorMessage}</p>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-[#1A1F2E] border border-gray-800 rounded-lg sm:rounded-lg overflow-hidden shadow-xl">
          <div className="bg-gradient-to-r from-[#128C7E]/10 to-transparent p-4 sm:p-6 border-b border-gray-800">
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <Send size={20} className="sm:w-6 sm:h-6 text-[#128C7E]" />
              {t('Add Balance')}
            </h2>
          </div>

          <div className="p-8">
            {/* User Selection */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                {t('Select User')}
              </label>
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  disabled={usersLoading}
                  className={`w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-${isRTL ? 'right' : 'left'} text-white flex items-center justify-between hover:border-gray-600 transition-colors ${
                    usersLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  <span className={selectedUser ? 'text-white' : 'text-gray-400'}>
                    {selectedUser ? `${selectedUser.username} (${selectedUser.email})` : t('Choose a user')}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`}
                  />
                </button>

                {showUserDropdown && (
                  <div className="absolute top-full mt-2 w-full bg-[#0B0E14] border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    {users.length === 0 ? (
                      <div className="p-4 text-gray-400 text-center">{t('No users found')}</div>
                    ) : (
                      users.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-[#128C7E]/10 text-gray-300 hover:text-white transition-colors border-b border-gray-800 last:border-b-0"
                        >
                          <div className="font-medium">{user.username}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Amount Input */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                {t('Amount (OTPs)')}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-semibold">
                  <span className="font-semibold">#</span>
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t('Enter OTP amount')}
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                />
              </div>
            </div>

            {/* Message Input */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                {t('Custom Message (Optional)')}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('Enter a custom message...')}
                rows="4"
                className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleClear}
                disabled={loading}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('Clear')}
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={!selectedUser || !amount || loading}
                className="px-6 py-3 bg-[#128C7E] hover:bg-[#0F6B63] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('Processing...') : t('Add Balance')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="bg-[#1A1F2E] border border-gray-800 rounded-lg shadow-2xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#128C7E]/10 to-transparent p-6 border-b border-gray-800">
              <h3 className="text-lg font-bold text-white">{t('Confirm Balance Addition')}</h3>
            </div>

            <div className="p-6">
              <div className="bg-[#0B0E14] rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-400">{t('User')}:</span>
                  <span className="font-semibold text-[#128C7E]">{selectedUser?.username}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-400">{t('Amount')}:</span>
                  <span className="font-semibold text-emerald-400">${parseFloat(amount).toFixed(2)}</span>
                </div>
                {message && (
                  <div className="border-t border-gray-700 pt-3">
                    <span className="text-gray-400 text-sm">{t('Message')}:</span>
                    <p className="text-gray-300 text-sm mt-1 italic">{message}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={handleAddBalance}
                  disabled={loading}
                  className="px-4 py-2 bg-[#128C7E] hover:bg-[#0F6B63] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? t('Processing...') : t('Confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}


